import { IDropdownData } from 'models/Common'
import {
  IShift,
  IShiftIncludeParams,
  SHIFT_AUTO_ACCEPT_STATUS,
  SHIFT_FOLLOWED,
  SHIFT_PROGRESS,
  SHIFT_STATUS,
  SHIFT_TYPE,
} from 'models/Shift'
import { differenceInHours, differenceInMinutes, format } from 'date-fns'
import { FEATURE_TOGGLE_RESTRICT_LAST_MINUTE_CANDIDATE_ADDITIONS_IN_THE_VMS } from 'config/featureToggles'
import AuthService from 'services/AuthService'
import { LAST_MINUTE_ADDITIONS_ALLOWED_USERS } from 'config/envVariables'
import { IScheduleSectionFormValues } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/schedule-section.types'
import {
  IModalPostFromTemplateShiftValues,
  TEditDuplicateShiftValues,
  IModalEditShiftValues,
  IModalDuplicateShiftValues,
} from 'ui/components/Admin/Shifts/ShiftEditDuplicate/utils/types'
import { useSelector } from 'react-redux'
import { selectCurrentProperties } from 'data/Properties/selectors'
import { calculateShiftDurationHours } from 'ui/components/Admin/Shifts/ShiftEditDuplicate/utils'
import { fromZonedTime } from 'date-fns-tz'
import { DATE_FORMAT_STRING, SHIFT_FILLED } from './constants/constants'

export const progressDropdownOptions = [
  { value: SHIFT_PROGRESS.NOT_STARTED, label: 'Not Started', isDefault: true },
  { value: SHIFT_PROGRESS.ACTIVE, label: 'Started' },
  { value: SHIFT_PROGRESS.FINISHED, label: 'Finished' },
]

export const filledDropdownOptions = [
  { value: -1, label: 'Filled/Not filled', isDefault: true },
  { value: SHIFT_FILLED.NOT_FILLED, label: 'Not filled' },
  { value: SHIFT_FILLED.FILLED, label: 'Filled' },
]

export const initialFilledOptionMapper = [
  { value: SHIFT_FILLED.NOT_FILLED, label: 'Not filled' },
  { value: SHIFT_FILLED.FILLED, label: 'Filled' },
]

export const followDropdownOptions = [
  { value: -1, label: 'Followed/Not followed', isDefault: true },
  { value: SHIFT_FOLLOWED.FOLLOWED, label: 'Followed' },
]

export const initialFollowedOptionMapper = [
  { value: SHIFT_FOLLOWED.NOT_FOLLOWED, label: 'Not followed' },
  { value: SHIFT_FOLLOWED.FOLLOWED, label: 'Followed' },
]

export const shiftTypeDropdownOptions = [
  { value: -1, label: 'Single/Multiday', isDefault: true },
  { value: SHIFT_TYPE.SINGLEDAY, label: 'Single' },
  { value: SHIFT_TYPE.MULTIDAY, label: 'Multiday' },
]

export const initialShiftTypeOptionMapper = [
  { value: SHIFT_TYPE.SINGLEDAY, label: 'Single' },
  { value: SHIFT_TYPE.MULTIDAY, label: 'Multiday' },
]

export const breakDropdownData: IDropdownData[] = [
  {
    value: 0,
    label: 'No Break',
  },
  {
    value: 30,
    label: '30 min',
  },
  {
    value: 45,
    label: '45 min',
  },
  {
    value: 60,
    label: '60 min',
  },
  {
    value: 75,
    label: '75 min',
  },
]

export const autoAcceptStatusOptions: IDropdownData[] = [
  {
    label: 'All',
    value: -1,
    isDefault: true,
  },
  {
    label: 'Accept',
    value: SHIFT_AUTO_ACCEPT_STATUS.ACCEPT,
  },
  {
    label: 'Auto Accept',
    value: SHIFT_AUTO_ACCEPT_STATUS.AUTO_ACCEPT,
  },
  {
    label: 'Selective Auto Accept',
    value: SHIFT_AUTO_ACCEPT_STATUS.SELECTIVE_AUTO_ACCEPT,
  },
]

export const getUpdateShiftSuccessMessageByStatus = (status?: SHIFT_STATUS) => {
  if (status === SHIFT_STATUS.DRAFT)
    return 'Your shift has been successfully saved as a draft. It will not be visible to Upshifters until you post it live.'

  return 'Shift updated successfully.'
}

export const checkIfShiftIsWithin24hours = (timeStart: string, propertyTimezone: string) => {
  const currentBrowserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const currentBrowserTime = new Date()

  const timeStartDate = new Date(timeStart)

  // Convert shift start time to UTC first
  const timeStartUtc = fromZonedTime(timeStartDate, propertyTimezone)

  // Convert current browser time to UTC for accurate comparison
  const currentUtcTime = fromZonedTime(currentBrowserTime, currentBrowserTimezone)

  // Calculate the difference in hours using UTC values (value rounded to floor)
  const hoursDifference = differenceInHours(timeStartUtc, currentUtcTime)
  return hoursDifference < 24
}

/**
 * Determines the minutes until shift starts
 * @param shift
 * @returns number
 */
export const getMinutesUntilShiftStarts = (shift: IShift) => {
  const shiftDateInTimezone = new Date().toLocaleString('en-US', {
    timeZone: shift.property_timezone,
  })
  return differenceInMinutes(new Date(shift.time_start), new Date(shiftDateInTimezone))
}

/**
 * Returns whether the logged in user can by pass the last minute additions restriction on a shift
 * @returns boolean
 */
export const canUserByPassLastMinuteAdditionsRestriction = () => {
  const loggedInUser = AuthService.getLoggedInUser()
  if (!loggedInUser) return false

  return (LAST_MINUTE_ADDITIONS_ALLOWED_USERS ?? '').split(',').includes(loggedInUser.email)
}

/**
 * Returns whether Last Minute Candidate Additions are restricted for the shift
 * @param shift
 * @returns boolean
 */
export const areLastMinuteAdditionsRestricted = (shift: IShift) => {
  if (!FEATURE_TOGGLE_RESTRICT_LAST_MINUTE_CANDIDATE_ADDITIONS_IN_THE_VMS) return false

  const minutesUntilShiftStarts = getMinutesUntilShiftStarts(shift)
  const restrictLastMinuteAdditions =
    shift.business?.vendor_management_system?.restrict_last_minute_additions ?? false

  const shiftFreezeTimeHours = shift.business?.vendor_management_system?.shift_freeze_hours || 3

  return restrictLastMinuteAdditions && minutesUntilShiftStarts < shiftFreezeTimeHours * 60
}

/**
 * Returns includes needed for SelectedShift - getSelectedShift, updateSelectedShift
 * @returns IShiftIncludeParams['include']
 */
export const getSelectedShiftIncludes = () => {
  const shiftIncludes: IShiftIncludeParams['include'] = [
    'followed_by_user',
    'business.business_setting',
    'gig_type.gig_type_categories',
    `property`,
    'rosters',
    'timesheets',
    'auto_accept_rosters',
    'days',
    'notes_count',
    'property.property_setting',
  ]
  if (FEATURE_TOGGLE_RESTRICT_LAST_MINUTE_CANDIDATE_ADDITIONS_IN_THE_VMS) {
    shiftIncludes.push('business.vendor_management_system')
  }
  return shiftIncludes
}

/**
 * Checks whether Selective Claim Mode (Selective Auto Accept/SAA) is enabled for a shift.
 * We consider Selective Claim Mode enabled for a shift, when one of the following settings are enabled for a shift:
 * "Auto-Accept By Favorited", "Auto-Accept by Worked before", "Auto-Accept by Certificates", "Auto-Accept by Onboarded"
 * @param shift
 * @returns boolean
 */
export const hasShiftSelectiveClaimModeEnabled = (
  shift: IShift | IModalEditShiftValues | IModalDuplicateShiftValues
) => {
  if (!shift) return false
  // In order to enable Selective Claim Mode for a shift, we need to enable Claim Mode (Auto Accept/AUTO) for the shift first.
  // We consider Claim Mode enabled for a shift, when "Auto Accept" setting is enabled for a shift.
  if (!shift.auto_accept) return false
  return (
    shift.auto_accept_by_certificates ||
    shift.auto_accept_by_onboarded ||
    shift.auto_accept_by_rosters
  )
}

/**
 * Function for checking whether a shift is a multiday shift
 * @param {IShift} shift
 * @returns {boolean}
 */
export const isMultidayShift = (shift: IShift) => Number(shift.type) === SHIFT_TYPE.MULTIDAY

/**
 * Function for checking whether a shift is a singleday shift
 * @param {IShift} shift
 * @returns {boolean}
 */
export const isSingledayShift = (shift: IShift) => Number(shift.type) === SHIFT_TYPE.SINGLEDAY

/**
 * Function that checks if the shift is over 8 hours long and the pay rate is less than the exempt rate.
 * If the conditions are met, a warning alert should be shown.
 */
export const shouldShowOTPolicyWarning = (
  values: TEditDuplicateShiftValues,
  payRateLimit: number,
  isEdit?: boolean
) => {
  // Safeguard for post from template, returns a values object with missing parameters
  if (values.type === undefined) {
    return false
  }

  const checkOvertimeWarning = (payValue: string, hoursDuration: number): boolean => {
    return Number(payValue) < payRateLimit && hoursDuration > 8
  }

  if (values.type === SHIFT_TYPE.SINGLEDAY) {
    if (isEdit) {
      const hoursDuration = calculateShiftDurationHours(
        values.date_start,
        values.hour_start,
        values.hour_end
      )
      return checkOvertimeWarning(values.pay_value, hoursDuration)
    }

    const singleDayShifts = (values as IModalPostFromTemplateShiftValues).single_day_shifts
    return singleDayShifts.some((day) => {
      const hoursDuration = calculateShiftDurationHours(day.id, day.hour_start, day.hour_end)
      return checkOvertimeWarning(day.pay_value, hoursDuration)
    })
  }

  // Multiday
  const days = values.to_hire ? values.individual_days : values.days
  return days.some((day) => {
    const hoursDuration = calculateShiftDurationHours(day.date_start, day.hour_start, day.hour_end)
    return checkOvertimeWarning(day.pay_value, hoursDuration)
  })
}

export const useOTPolicyWarning = (values: TEditDuplicateShiftValues, isEdit?: boolean) => {
  const properties = useSelector(selectCurrentProperties)
  const selectedProperty = properties.find((property) => property.id === values.property_id)
  const payRateLimit = selectedProperty?.active_overtime_policy?.pay_rate_limit

  return {
    showOTPolicyWarning: payRateLimit
      ? shouldShowOTPolicyWarning(values, payRateLimit, isEdit)
      : false,
    payRateLimit: payRateLimit ?? 0,
  }
}

/**
 * Function that checks if the shift is over 8 hours long and the pay rate is less than the exempt rate.
 * If the conditions are met, a warning alert should be shown.
 */
export const shouldShowOTPolicyWarningV2 = (
  values: IScheduleSectionFormValues,
  payRateLimit: number,
  isEdit?: boolean
) => {
  const checkOvertimeWarning = (payValue: string, hoursDuration: number): boolean => {
    return Number(payValue) < payRateLimit && hoursDuration > 8
  }

  if (values.type === SHIFT_TYPE.SINGLEDAY) {
    if (isEdit) {
      const hoursDuration = calculateShiftDurationHours(
        values.date_start,
        values.hour_start,
        values.hour_end
      )
      return checkOvertimeWarning(values.pay_value, hoursDuration)
    }

    const singleDayShifts = values.single_day_shifts
    return singleDayShifts.some((day) => {
      const hoursDuration = calculateShiftDurationHours(day.id, day.hour_start, day.hour_end)
      return checkOvertimeWarning(day.pay_value, hoursDuration)
    })
  }

  // Multiday
  const days = values.days
  return days.some((day) => {
    const hoursDuration = calculateShiftDurationHours(day.date_start, day.hour_start, day.hour_end)
    return checkOvertimeWarning(day.pay_value, hoursDuration)
  })
}

export const isShiftShort = (shiftValues: any[]) => {
  if (Array.isArray(shiftValues) && shiftValues.length) {
    return shiftValues.filter(
      (shift) =>
        calculateShiftDurationHours(
          format(new Date(shift.date_start), DATE_FORMAT_STRING.MM_dd_yyyy),
          shift.hour_start,
          shift.hour_end
        ) < 5 && shift.break > 0
    ).length
  }
  return false
}

/**
 * Determines whether the Invite Upshifters button is disabled or not for a shift
 * @param shift
 * @returns boolean
 */
export const isInviteUpshiftersBtnDisabled = (shift: IShift) => {
  const minutesUntilShiftStarts = getMinutesUntilShiftStarts(shift)

  return (
    minutesUntilShiftStarts < 30 ||
    shift.status === SHIFT_STATUS.DRAFT ||
    shift.progress === SHIFT_PROGRESS.ACTIVE ||
    shift.progress === SHIFT_PROGRESS.FINISHED
  )
}
